import { useCallback, useEffect, useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../context/AuthContext';
import { useAssetOverview } from '../../../../context/AssetOverviewContext';
import { useAssetStatistics } from '../../../../hooks/useAssetStatistics';
import { useCloseOnMapPointerDown } from '../../../../hooks/useCloseOnMapPointerDown';
import { getAssetManagerErrorMessage } from '../../../../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../../../../services/AssetManager/assetManager.constants';
import { AllowedRoles } from '../../../../routes/routeConstants/allowedRoles';
import Notification from '../../../../shared/components/Notification';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import AssetStatisticWidget from './AssetStatisticWidget';
import SetUpStatisticPopover from './SetUpStatisticPopover';
import styles from './assetStatisticsBar.module.scss';

// Widgets shown before the row scrolls sideways; must match $visible-statistics in the stylesheet.
const VISIBLE_STATISTICS = 6;

// `session` keys the form, so every open starts fresh and anything left unsaved is discarded.
interface SetupState {
  open: boolean;
  statisticId: number | null;
  session: number;
}

const INITIAL_SETUP: SetupState = { open: false, statisticId: null, session: 0 };

const AssetStatisticsBar = () => {
  const { canAccess } = useAuth();
  const canManageWidgets = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });
  const { activeStatisticId, toggleStatistic, clearStatistic } = useAssetOverview();
  const { statistics, maxStatistics, statisticsError, isLoadingStatistics, removeStatistic } = useAssetStatistics();
  const [setup, setSetup] = useState<SetupState>(INITIAL_SETUP);

  const isAtMax = maxStatistics !== undefined && statistics.length >= maxStatistics;
  const isScrollable = statistics.length > VISIBLE_STATISTICS;
  const editingStatistic = statistics.find(({ id }) => id === setup.statisticId);

  useEffect(() => {
    if (!statisticsError) return;
    Notification({
      message: assetManagerServiceMessages.STATISTICS.GET_ERROR,
      description: getAssetManagerErrorMessage(statisticsError),
      type: NotificationTypes.ERROR,
    });
  }, [statisticsError]);

  // The active filter can't outlive its widget: removed, or broken on a later refresh.
  const isActiveUsable = statistics.some(({ id, error }) => id === activeStatisticId && !error);
  useEffect(() => {
    if (activeStatisticId !== null && !isLoadingStatistics && !isActiveUsable) clearStatistic();
  }, [activeStatisticId, isActiveUsable, isLoadingStatistics, clearStatistic]);

  const openSetup = useCallback(
    (statisticId: number | null) => setSetup((current) => ({ open: true, statisticId, session: current.session + 1 })),
    [],
  );

  const handleCloseSetup = useCallback(() => setSetup((current) => ({ ...current, open: false })), []);

  const handleSetupOpenChange = (open: boolean) => (open ? openSetup(null) : handleCloseSetup());

  useCloseOnMapPointerDown(setup.open, handleCloseSetup);

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <ConditionalRender visible={activeStatisticId !== null}>
          <Button className={styles.clearFiltersButton} onClick={clearStatistic}>
            Clear Filters
          </Button>
        </ConditionalRender>
      </div>
      <div className={styles.widgets}>
        <div className={isScrollable ? styles.widgetListScrollable : styles.widgetList}>
          {statistics.map((statistic) => (
            <AssetStatisticWidget
              key={statistic.id}
              statistic={statistic}
              isSelected={statistic.id === activeStatisticId}
              canManage={canManageWidgets}
              onSelect={toggleStatistic}
              onEdit={openSetup}
              onRemove={removeStatistic}
            />
          ))}
        </div>
        <ConditionalRender visible={canManageWidgets && !isLoadingStatistics}>
          <Popover
            trigger="click"
            open={setup.open}
            onOpenChange={handleSetupOpenChange}
            placement="bottomRight"
            arrow={false}
            destroyTooltipOnHide
            overlayClassName={styles.setupPopoverOverlay}
            content={<SetUpStatisticPopover key={setup.session} statistic={editingStatistic} onClose={handleCloseSetup} />}
          >
            <Tooltip title={isAtMax ? `Maximum of ${maxStatistics} statistics reached` : undefined}>
              <button type="button" className={styles.addWidget} disabled={isAtMax}>
                <PlusCircleOutlined className={styles.plusIcon} />
                <span className={styles.addLabel}>Add Statistics</span>
              </button>
            </Tooltip>
          </Popover>
        </ConditionalRender>
      </div>
    </div>
  );
};

export default AssetStatisticsBar;
